// frontend/src/components/staff/AdminSetAvailabilityModal.js
//
// Workforce — SCHED-SLICE1-K admin-set-staff-availability modal (compromise
// modal — CREATE-mode default + LIST-mode body-swap for full per-staff CRUD).
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §10K
// (sub-sections §10K.1 through §10K.9). Visual DoD at §10K.8 (25 checks).
// Tech contract: docs/architecture/SCHED-SLICE1-K-rbac-lock.md
//                (Marcus Option A — admin-namespaced endpoints).
// Story:       docs/backlog/SCHED-SLICE1-K-admin-set-staff-availability.md
//
// Mounted as a child of EditShiftModal — opened by clicking "Set availability
// for [first name]" inside the availability-conflict banner row (Scenario S11
// + visual spec §10K.1). The launching shift's day + start/end time pre-fill
// the CREATE form.
//
// One-modal-context end-to-end:
//   - Default = CREATE mode (Scenario S12).
//   - "Manage all availability for [first name] >" link swaps body to LIST
//     mode (Scenarios S13 + S14 expander entry; visual spec §10K.3).
//   - Edit on a LIST row expands the row inline (visual spec §10K.4); other
//     rows dim and disable to keep one-task-per-modal hygiene.
//   - Delete opens a delete-confirm sub-modal stacked over the parent
//     (visual spec §10K.5); confirm fires DELETE; parent stays open at LIST.
//   - Empty state (visual spec §10K.6) when staff has zero records on file.
//
// Production-safety:
//   - Pure-additive component; no existing surface mutated.
//   - Single-revert posture (delete this file + revert two import sites).
//   - No env vars, no schema changes; consumes `workforceAdminAvailabilityAPI`
//     locked at `docs/architecture/SCHED-SLICE1-K-rbac-lock.md`.
//   - NOT a Customer-Facing Gate (R-020) — admin-internal feature.
//   - Hard Rule § N/A — no customer surface.
//   - Defense-in-depth: server NEVER trusts client-supplied `staffProfileId`
//     (Marcus RBAC LOCK invariant §1); the FE never passes one. Target staff
//     identity comes from the `:userId` path param only.
//
// Dev-fixture flag for offline iteration while SCHED-1K-BE is in Simone's
// queue: setting `localStorage.SCHED_ADMIN_AVAIL_DEV_FIXTURES === 'true'`
// returns an in-memory list scoped to this modal session (mirrors the
// SCHED-1F / 1G / 1H fixture-gate pattern). When the BE lands, remove this
// gate in the same PR or a fast-follow.

import React from "react";
import { workforceAdminAvailabilityAPI } from "../../services/api";
import "./AdminSetAvailabilityModal.css";

const DAY_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// HH:MM 24-hour pattern — mirrors StaffAvailabilityPage HHMM_PATTERN and
// the BE `workforceStaffShiftsController.HHMM_PATTERN`. The FE fast-fails
// before submit; the BE re-validates per Marcus RBAC LOCK §"Endpoint specs".
const HHMM_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const MAX_NOTES_LENGTH = 240;

const dayLabel = (value) => {
  const entry = DAY_OPTIONS.find((d) => d.value === value);
  return entry ? entry.label : `Day ${value}`;
};

// Format an HH:MM 24-hour string as "9:00 AM MST" / "5:30 PM MST" — same
// formatter as StaffAvailabilityPage.formatTime12h (codebase-wide MST
// convention; no moment dependency for clock-time-of-day formatting).
const formatTime12h = (hhmm) => {
  if (typeof hhmm !== "string" || !HHMM_PATTERN.test(hhmm)) return hhmm || "";
  const [h, m] = hhmm.split(":").map((part) => parseInt(part, 10));
  const period = h < 12 ? "AM" : "PM";
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m).padStart(2, "0")} ${period} MST`;
};

// Default effectiveDate when admin opens CREATE mode without a pre-fill —
// today at Phoenix-midnight, sent as ISO. Same shape as the staff-self-serve
// page (StaffAvailabilityPage.todayIsoMidnightUtc).
const todayIsoMidnightUtc = () => {
  const now = new Date();
  const ymd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(now.getDate()).padStart(2, "0")}`;
  return `${ymd}T00:00:00.000Z`;
};

// SCHED-AVAIL-DASHBOARD-002-B — One-off mode helpers.
//
// Build "today" as a YYYY-MM-DD string in the user's local frame. The BE
// re-validates against Phoenix-MST so this is just a sensible default for
// the date picker; admin can correct it before save and the BE-side
// `ONE_OFF_DAY_OF_WEEK_MISMATCH` defense-in-depth gate (PR #153) catches any
// residual mismatch.
const todayYmd = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(now.getDate()).padStart(2, "0")}`;
};

const YYYY_MM_DD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Convert a "YYYY-MM-DD" calendar date into a Phoenix-MST day-of-week
// (0=Sun..6=Sat). Phoenix is UTC-7 year-round (no DST), so we anchor the
// date at 12:00 wall-clock to avoid any local-time-vs-UTC boundary error.
//
// Returns null when the input does not match the strict shape (caller
// should treat as "cannot derive — let the BE 400 with ONE_OFF_DAY_OF_WEEK_
// MISMATCH if it gets that far"; the FE save-button disable guard already
// blocks empty / invalid dates).
const dayOfWeekForYmd = (ymd) => {
  if (typeof ymd !== "string" || !YYYY_MM_DD_PATTERN.test(ymd)) return null;
  const [yStr, mStr, dStr] = ymd.split("-");
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10);
  const d = parseInt(dStr, 10);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  // 12:00 Phoenix-MST (-07:00) === 19:00 UTC same calendar date. Building
  // the Date in UTC at 19:00 keeps the calendar-day stable regardless of
  // the local frame jsdom / a browser runs in.
  const dt = new Date(Date.UTC(y, m - 1, d, 19, 0, 0, 0));
  if (isNaN(dt.getTime())) return null;
  // Use getUTCDay() against the constructed instant — the +12:00 anchor
  // means UTC day-of-week === Phoenix day-of-week for this calendar date.
  // Round-trip verify (catches Date overflow like 2026-02-30 -> Mar 2).
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== m - 1 ||
    dt.getUTCDate() !== d
  ) {
    return null;
  }
  return dt.getUTCDay();
};

// Convert a "YYYY-MM-DD" calendar date into an ISO 8601 instant at
// 00:00:00.000 Phoenix-MST (== 07:00:00.000Z UTC). Mirrors the BE
// `parseWeekStart()` convention in workforceAdminAvailabilityDashboardController.js
// so the BE round-trip produces a self-consistent dayOfWeek.
const ymdToPhoenixMidnightIso = (ymd) => {
  if (typeof ymd !== "string" || !YYYY_MM_DD_PATTERN.test(ymd)) return null;
  return `${ymd}T07:00:00.000Z`;
};

// Format a YYYY-MM-DD as "Mon, May 24, 2026" for human-readable display
// alongside the picker — matches Zuri spec §3.3 ("format displayed").
const DAY_FULL_DISPLAY = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
];
const MONTH_DISPLAY = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const formatYmdHuman = (ymd) => {
  if (typeof ymd !== "string" || !YYYY_MM_DD_PATTERN.test(ymd)) return ymd || "";
  const dow = dayOfWeekForYmd(ymd);
  const [, mStr, dStr] = ymd.split("-");
  const m = parseInt(mStr, 10);
  const d = parseInt(dStr, 10);
  const dowLabel = dow != null ? DAY_FULL_DISPLAY[dow] : "";
  const mLabel = MONTH_DISPLAY[m - 1] || "";
  // "Mon, May 24, 2026"
  return `${dowLabel ? dowLabel + ", " : ""}${mLabel} ${d}, ${ymd.slice(0, 4)}`;
};

// Pull the first token of a name for the affordance label / link copy.
// "Jamie Smith" -> "Jamie". Empty/missing -> empty string.
const firstName = (full) => {
  if (typeof full !== "string") return "";
  const trimmed = full.trim();
  if (!trimmed) return "";
  return trimmed.split(/\s+/)[0];
};

// Build the CREATE-mode default form, optionally pre-filled from a
// launching shift's day + window. Pre-fill is a SUGGESTION — admin can
// change before save (visual spec §10K.2).
//
// SCHED-AVAIL-DASHBOARD-002-B prefill extension:
//   - prefill.isRecurring === false enters "Single occurrence" mode at open
//   - prefill.effectiveDate ("YYYY-MM-DD") seeds the date picker; if missing,
//     falls back to today
//   - In one-off mode the day-of-week is DERIVED from effectiveDate, NOT from
//     prefill.dayOfWeek (defense-in-depth — admin can't accidentally save
//     a mismatched pair)
const buildInitialCreateForm = ({ prefill }) => {
  if (prefill && typeof prefill === "object") {
    const isOneOff = prefill.isRecurring === false;
    const baseStart =
      typeof prefill.startTime === "string" && HHMM_PATTERN.test(prefill.startTime)
        ? prefill.startTime
        : "09:00";
    const baseEnd =
      typeof prefill.endTime === "string" && HHMM_PATTERN.test(prefill.endTime)
        ? prefill.endTime
        : "17:00";
    if (isOneOff) {
      const ymd =
        typeof prefill.effectiveDate === "string" &&
        YYYY_MM_DD_PATTERN.test(prefill.effectiveDate)
          ? prefill.effectiveDate
          : todayYmd();
      const derivedDow = dayOfWeekForYmd(ymd);
      return {
        dayOfWeek: derivedDow != null ? derivedDow : 1,
        startTime: baseStart,
        endTime: baseEnd,
        isRecurring: false,
        effectiveDateYmd: ymd,
        notes: "",
      };
    }
    return {
      dayOfWeek:
        typeof prefill.dayOfWeek === "number" ? prefill.dayOfWeek : 1,
      startTime: baseStart,
      endTime: baseEnd,
      isRecurring: true,
      effectiveDateYmd: todayYmd(),
      notes: "",
    };
  }
  return {
    dayOfWeek: 1,
    startTime: "09:00",
    endTime: "17:00",
    isRecurring: true,
    effectiveDateYmd: todayYmd(),
    notes: "",
  };
};

// Dev-fixture in-memory store, scoped per (userId) for the session. Only
// active when `localStorage.SCHED_ADMIN_AVAIL_DEV_FIXTURES === 'true'`.
// Mirrors the SCHED-1F / 1G / 1H gate pattern.
const devFixturesEnabled = () => {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage &&
      window.localStorage.getItem("SCHED_ADMIN_AVAIL_DEV_FIXTURES") === "true"
    );
  } catch (e) {
    return false;
  }
};

const __devFixtureStore = new Map(); // userId -> array of fake Availability rows

const devGenerateId = () =>
  `dev-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const devList = (userId) => {
  const rows = __devFixtureStore.get(String(userId)) || [];
  return Promise.resolve({ data: { availability: rows.slice() } });
};

const devCreate = (userId, body) => {
  const rows = __devFixtureStore.get(String(userId)) || [];
  const next = {
    _id: devGenerateId(),
    staffProfileId: `dev-profile-${userId}`,
    setByAdmin: true,
    createdAt: new Date().toISOString(),
    ...body,
  };
  rows.push(next);
  __devFixtureStore.set(String(userId), rows);
  return Promise.resolve({ data: { availability: next } });
};

const devUpdate = (userId, availabilityId, body) => {
  const rows = __devFixtureStore.get(String(userId)) || [];
  const idx = rows.findIndex((r) => r._id === availabilityId);
  if (idx === -1) {
    return Promise.reject({ response: { status: 404 } });
  }
  rows[idx] = { ...rows[idx], ...body };
  __devFixtureStore.set(String(userId), rows);
  return Promise.resolve({ data: { availability: rows[idx] } });
};

const devRemove = (userId, availabilityId) => {
  const rows = __devFixtureStore.get(String(userId)) || [];
  const next = rows.filter((r) => r._id !== availabilityId);
  __devFixtureStore.set(String(userId), next);
  return Promise.resolve({ status: 204 });
};

const callList = (userId) =>
  devFixturesEnabled()
    ? devList(userId)
    : workforceAdminAvailabilityAPI.list(userId);

const callCreate = (userId, body) =>
  devFixturesEnabled()
    ? devCreate(userId, body)
    : workforceAdminAvailabilityAPI.create(userId, body);

const callUpdate = (userId, availabilityId, body) =>
  devFixturesEnabled()
    ? devUpdate(userId, availabilityId, body)
    : workforceAdminAvailabilityAPI.update(userId, availabilityId, body);

const callRemove = (userId, availabilityId) =>
  devFixturesEnabled()
    ? devRemove(userId, availabilityId)
    : workforceAdminAvailabilityAPI.remove(userId, availabilityId);

/**
 * AdminSetAvailabilityModal
 *
 * Props:
 *   - open                 boolean — mount control
 *   - userId               string  — staff `User._id` (the path-param target;
 *                                    NOT `StaffProfile._id`). Required.
 *   - staffFullName        string  — full name for title + accessibility
 *                                    labels ("Jamie Smith"). Falls back to
 *                                    "this staff member" if missing.
 *   - prefill              { dayOfWeek, startTime, endTime } | null —
 *                                    optional launching-shift pre-fill for
 *                                    CREATE mode (Scenario S12 / §10K.2).
 *   - initialView          "create" | "list" (default "create") — which body
 *                                    swap the modal opens with. SCHED-AVAIL-
 *                                    DASHBOARD-001-B passes "list" when the
 *                                    admin clicks a staff-label cell or a
 *                                    filled day cell (matrix drill-down,
 *                                    AC Scenarios 10 + 12). Backward-
 *                                    compatible — EditShiftModal callers
 *                                    omit it and get CREATE mode as before.
 *   - anchorAvailabilityId string | null — when initialView is "list" and
 *                                    the matrix click targeted a specific
 *                                    window (Scenario 12), the LIST body
 *                                    scrolls that row into view and
 *                                    highlights the Edit button. No
 *                                    pre-mutation; admin still clicks Edit
 *                                    explicitly. Ignored in CREATE view.
 *   - onClose              () => void — dismiss handler.
 *   - onAvailabilityChanged() => void — bubble to parent so it can refetch
 *                                    conflict-detection state after a
 *                                    successful create / update / delete.
 */
const AdminSetAvailabilityModal = ({
  open,
  userId,
  staffFullName,
  prefill,
  initialView,
  anchorAvailabilityId,
  onClose,
  onAvailabilityChanged,
}) => {
  const safeFullName = typeof staffFullName === "string" && staffFullName.trim()
    ? staffFullName.trim()
    : "this staff member";
  const safeFirstName = firstName(safeFullName) || "staff";

  // --- View / mode state ---------------------------------------------------
  // 'create' | 'list' — body of the single modal swaps between them.
  // `initialView` (default "create") lets the SCHED-AVAIL-DASHBOARD-001-B
  // matrix open the modal directly in LIST mode without a body-swap click
  // (drill-down from staff-label cell or filled day cell — AC 10 + 12).
  // Backward-compatible: EditShiftModal callers omit `initialView` and get
  // CREATE-mode entry unchanged.
  const resolvedInitialView = initialView === "list" ? "list" : "create";
  const [view, setView] = React.useState(resolvedInitialView);

  // CREATE-mode form state — pre-filled from launching shift on mount.
  const [createForm, setCreateForm] = React.useState(() =>
    buildInitialCreateForm({ prefill })
  );
  const [createSubmitting, setCreateSubmitting] = React.useState(false);
  const [createFormError, setCreateFormError] = React.useState("");

  // LIST-mode data state — fetched lazily on view-swap (or on open if the
  // caller wants list-first; here we wait for the expander click).
  const [listRecords, setListRecords] = React.useState([]);
  const [listLoading, setListLoading] = React.useState(false);
  const [listError, setListError] = React.useState("");

  // Inline-edit state (one row at a time per §10K.4).
  const [editingId, setEditingId] = React.useState(null);
  const [editForm, setEditForm] = React.useState(null);
  const [editSubmitting, setEditSubmitting] = React.useState(false);
  const [editFormError, setEditFormError] = React.useState("");

  // Delete-confirm sub-modal state (§10K.5).
  const [pendingDelete, setPendingDelete] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  // Toast / success messaging — same single-slot pattern as
  // StaffAvailabilityPage; auto-dismiss after ~3s.
  const [toast, setToast] = React.useState(null);
  const toastTimerRef = React.useRef(null);
  const showToast = React.useCallback((variant, message) => {
    setToast({ variant, message });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 3000);
  }, []);

  // Reset state on open / re-open. Avoids stale form data when admin closes
  // and re-opens for a different staff member. View resets to whichever
  // entry-view the caller requested (`initialView` prop, default "create").
  React.useEffect(() => {
    if (!open) return;
    setView(initialView === "list" ? "list" : "create");
    setCreateForm(buildInitialCreateForm({ prefill }));
    setCreateSubmitting(false);
    setCreateFormError("");
    setListRecords([]);
    setListLoading(false);
    setListError("");
    setEditingId(null);
    setEditForm(null);
    setEditSubmitting(false);
    setEditFormError("");
    setPendingDelete(null);
    setDeleting(false);
    setToast(null);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  }, [open, prefill, initialView]);

  // Clean up toast timer on unmount.
  React.useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // Esc closes the active surface in priority order:
  //   delete-confirm > inline-edit > the whole modal.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (pendingDelete && !deleting) {
        setPendingDelete(null);
        return;
      }
      if (editingId && !editSubmitting) {
        setEditingId(null);
        setEditForm(null);
        setEditFormError("");
        return;
      }
      if (!createSubmitting && !editSubmitting && !deleting) {
        if (typeof onClose === "function") onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [
    open,
    onClose,
    pendingDelete,
    deleting,
    editingId,
    editSubmitting,
    createSubmitting,
  ]);

  // Sorted LIST view — defensively re-sort the records on render in case the
  // BE response shape drifts. Matches SCHED-1H pattern + §10K.3 ("sorted by
  // dayOfWeek asc then startTime asc").
  const sortedRecords = React.useMemo(() => {
    const copy = Array.isArray(listRecords) ? [...listRecords] : [];
    copy.sort((a, b) => {
      if (a.dayOfWeek !== b.dayOfWeek) return a.dayOfWeek - b.dayOfWeek;
      const at = a.startTime || "";
      const bt = b.startTime || "";
      return at.localeCompare(bt);
    });
    return copy;
  }, [listRecords]);

  // ---- Validation -------------------------------------------------------
  const validateForm = (form) => {
    if (!form) return false;
    const { dayOfWeek, startTime, endTime, notes, isRecurring } = form;
    if (
      typeof dayOfWeek !== "number" ||
      !Number.isInteger(dayOfWeek) ||
      dayOfWeek < 0 ||
      dayOfWeek > 6
    ) {
      return false;
    }
    if (!HHMM_PATTERN.test(startTime)) return false;
    if (!HHMM_PATTERN.test(endTime)) return false;
    // String compare on HH:MM 24-hour is lexicographically equivalent to
    // numeric time-of-day compare (mirrors BE validator + SCHED-1H).
    if (endTime <= startTime) return false;
    if (typeof notes === "string" && notes.length > MAX_NOTES_LENGTH) {
      return false;
    }
    // SCHED-AVAIL-DASHBOARD-002-B — one-off mode requires a valid date.
    // The derived dayOfWeek above is computed from effectiveDateYmd so the
    // pair is always consistent before submit; this guard catches the empty
    // / malformed cases.
    if (isRecurring === false) {
      const ymd = form.effectiveDateYmd;
      if (typeof ymd !== "string" || !YYYY_MM_DD_PATTERN.test(ymd)) {
        return false;
      }
      // dayOfWeek MUST match the derived day-of-week for the picked date
      // (defense-in-depth; the form-state setter already keeps these in
      // sync but the BE will 400 ONE_OFF_DAY_OF_WEEK_MISMATCH if drift slips
      // through). FE blocks the save so the BE 400 stays unreachable from
      // happy-path UI per story Scenario 10.
      const derivedDow = dayOfWeekForYmd(ymd);
      if (derivedDow == null || derivedDow !== dayOfWeek) return false;
    }
    return true;
  };
  const createValid = validateForm(createForm);
  const editValid = validateForm(editForm);

  const timeRangeErrorFor = (form) => {
    if (!form) return "";
    if (!HHMM_PATTERN.test(form.startTime || "")) return "";
    if (!HHMM_PATTERN.test(form.endTime || "")) return "";
    if (form.endTime <= form.startTime) {
      return "End time must be after start time.";
    }
    return "";
  };

  // ---- API helpers ------------------------------------------------------
  const fetchList = React.useCallback(async () => {
    if (!userId) return;
    setListLoading(true);
    setListError("");
    try {
      const res = await callList(userId);
      const list = Array.isArray(res?.data?.availability)
        ? res.data.availability
        : [];
      setListRecords(list);
    } catch (err) {
      setListError(
        "Could not load this staff member's availability. Please try again."
      );
    } finally {
      setListLoading(false);
    }
  }, [userId]);

  // Caller-driven LIST-mode entry: when SCHED-AVAIL-DASHBOARD-001-B opens
  // the modal with `initialView="list"`, fetch the staff member's full
  // availability list immediately so the LIST body renders populated
  // instead of waiting for the in-modal "Manage all availability" expander
  // click. EditShiftModal callers (which omit `initialView`) keep the lazy
  // fetch-on-expander-click behavior — fetchList is only triggered here
  // when the caller explicitly requested LIST entry.
  React.useEffect(() => {
    if (!open) return;
    if (initialView !== "list") return;
    if (!userId) return;
    fetchList();
  }, [open, userId, initialView, fetchList]);

  // Anchor scroll for matrix drill-down on a filled cell (AC Scenario 12):
  // when the caller passes `anchorAvailabilityId` along with `initialView=
  // "list"`, scroll that row into view and visually highlight its Edit
  // button so the admin sees which window the matrix click targeted. No
  // pre-mutation; the admin clicks Edit explicitly to mutate.
  const anchorRowRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    if (initialView !== "list") return;
    if (!anchorAvailabilityId) return;
    if (listLoading) return;
    if (!Array.isArray(listRecords) || listRecords.length === 0) return;
    const node = anchorRowRef.current;
    if (!node || typeof node.scrollIntoView !== "function") return;
    try {
      node.scrollIntoView({ block: "center", behavior: "smooth" });
    } catch (e) {
      try {
        node.scrollIntoView();
      } catch (_e) {
        // jsdom + some envs lack scrollIntoView; silent — highlight class
        // still renders and Nia can verify visually.
      }
    }
  }, [
    open,
    initialView,
    anchorAvailabilityId,
    listLoading,
    listRecords,
  ]);

  // ---- CREATE-mode handlers --------------------------------------------
  const handleCreateField = (field, value) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };

  // SCHED-AVAIL-DASHBOARD-002-B — segmented "Window type" toggle. Switching
  // mode preserves time-input values (Zuri §3.3 spec) but resets the
  // mode-specific bits (date-picker on entry to one-off; dayOfWeek on exit
  // from one-off and re-entry to recurring).
  const switchWindowType = (nextIsRecurring) => {
    setCreateForm((prev) => {
      if (!!prev.isRecurring === !!nextIsRecurring) return prev;
      if (nextIsRecurring) {
        return {
          ...prev,
          isRecurring: true,
          // Day-of-week resets to Monday on switch to recurring (Zuri §3.3:
          // "date / day-of-week values cleared across mode switches").
          dayOfWeek: 1,
        };
      }
      const ymd = prev.effectiveDateYmd || todayYmd();
      const derivedDow = dayOfWeekForYmd(ymd);
      return {
        ...prev,
        isRecurring: false,
        effectiveDateYmd: ymd,
        dayOfWeek: derivedDow != null ? derivedDow : prev.dayOfWeek,
      };
    });
    setCreateFormError("");
  };

  // SCHED-AVAIL-DASHBOARD-002-B — when admin edits the one-off date, derive
  // dayOfWeek from the new date so the pair stays in sync (Scenario 10
  // "auto-correct preferred" — Kenya's friendlier-UX pick).
  const handleEffectiveDateChange = (ymd) => {
    setCreateForm((prev) => {
      const derivedDow = dayOfWeekForYmd(ymd);
      return {
        ...prev,
        effectiveDateYmd: ymd,
        dayOfWeek: derivedDow != null ? derivedDow : prev.dayOfWeek,
      };
    });
  };

  const buildCreateBody = () => {
    const trimmedNotes =
      typeof createForm.notes === "string" ? createForm.notes.trim() : "";
    const isRecurring = !!createForm.isRecurring;
    const body = {
      dayOfWeek: createForm.dayOfWeek,
      startTime: createForm.startTime,
      endTime: createForm.endTime,
      isRecurring,
    };
    if (isRecurring) {
      // Slice 1 behavior — recurring effectiveDate is "now" at Phoenix-midnight.
      body.effectiveDate = todayIsoMidnightUtc();
    } else {
      // SCHED-AVAIL-DASHBOARD-002-B — one-off shape: effectiveDate === endDate
      // (both Phoenix-MST midnight) so the BE one-off validator passes. The
      // BE auto-fills endDate when omitted, but we send it explicitly for
      // wire-shape clarity + so a server that doesn't yet have the auto-fill
      // patch still validates cleanly.
      const iso = ymdToPhoenixMidnightIso(createForm.effectiveDateYmd);
      body.effectiveDate = iso;
      body.endDate = iso;
    }
    if (trimmedNotes.length > 0) body.notes = trimmedNotes;
    return body;
  };

  const handleCreateSubmit = async (event) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }
    if (!createValid || createSubmitting || !userId) return;
    setCreateFormError("");
    setCreateSubmitting(true);
    try {
      await callCreate(userId, buildCreateBody());
      showToast("success", `Availability set for ${safeFullName}.`);
      if (typeof onAvailabilityChanged === "function") {
        try {
          onAvailabilityChanged();
        } catch (e) {
          /* parent's problem; don't break this modal */
        }
      }
      // CREATE-mode default behavior on Save success (§10K.2 States): modal
      // dismisses. Schedule Builder warning panel refresh is the parent's
      // responsibility (via onAvailabilityChanged + the 30-sec poll).
      if (typeof onClose === "function") onClose();
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const fallback =
        status >= 500
          ? "Could not save availability — try again."
          : "Could not save availability. Please check the form and try again.";
      // SCHED-AVAIL-DASHBOARD-002-B — surface BE one-off validation codes
      // with friendlier copy. The FE save-guard normally blocks these from
      // reaching the BE, but they still serve as defense-in-depth for any
      // direct-API caller (and for the edge case where the FE clock drifts
      // and the BE rejects on Phoenix-MST boundary).
      const oneOffCopy =
        code === "ONE_OFF_END_DATE_MISMATCH"
          ? "One-off availability must be a single date — set the date and try again."
          : code === "ONE_OFF_DAY_OF_WEEK_MISMATCH"
          ? "The selected date's day of the week doesn't match — pick the date again to refresh."
          : null;
      const message =
        oneOffCopy ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        fallback;
      setCreateFormError(message);
    } finally {
      setCreateSubmitting(false);
    }
  };

  // ---- LIST-mode handlers ----------------------------------------------
  const openListMode = () => {
    setView("list");
    fetchList();
  };
  const backToCreate = () => {
    // Preserve CREATE form values per §10K.3 ("admin's pre-filled inputs
    // are PRESERVED — don't lose work").
    setView("create");
    setEditingId(null);
    setEditForm(null);
    setEditFormError("");
  };
  const setAnotherWindow = () => {
    setCreateForm(buildInitialCreateForm({ prefill: null }));
    setView("create");
    setEditingId(null);
    setEditForm(null);
    setEditFormError("");
  };

  // ---- Inline-edit handlers --------------------------------------------
  const openEdit = (record) => {
    setEditingId(record._id);
    setEditForm({
      dayOfWeek: record.dayOfWeek,
      startTime: record.startTime,
      endTime: record.endTime,
      isRecurring: !!record.isRecurring,
      notes: typeof record.notes === "string" ? record.notes : "",
    });
    setEditFormError("");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
    setEditFormError("");
  };
  const handleEditField = (field, value) => {
    setEditForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };
  const buildEditBody = () => {
    const trimmedNotes =
      typeof editForm.notes === "string" ? editForm.notes.trim() : "";
    const body = {
      dayOfWeek: editForm.dayOfWeek,
      startTime: editForm.startTime,
      endTime: editForm.endTime,
      isRecurring: !!editForm.isRecurring,
    };
    // Sending null clears any previous notes value on partial-update.
    body.notes = trimmedNotes.length > 0 ? trimmedNotes : null;
    return body;
  };
  const handleEditSubmit = async (event) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }
    if (!editValid || editSubmitting || !editingId || !userId) return;
    setEditFormError("");
    setEditSubmitting(true);
    try {
      const res = await callUpdate(userId, editingId, buildEditBody());
      const updated = res?.data?.availability;
      // Optimistic inline update — splice the changed record into the
      // local list so the row collapses to display mode with the new
      // values without a refetch round-trip.
      if (updated && updated._id) {
        setListRecords((prev) =>
          prev.map((r) => (r._id === updated._id ? { ...r, ...updated } : r))
        );
      } else {
        setListRecords((prev) =>
          prev.map((r) =>
            r._id === editingId ? { ...r, ...buildEditBody() } : r
          )
        );
      }
      setEditingId(null);
      setEditForm(null);
      showToast("success", `Availability updated for ${safeFullName}.`);
      if (typeof onAvailabilityChanged === "function") {
        try {
          onAvailabilityChanged();
        } catch (e) {
          /* parent's problem */
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      const fallback =
        status >= 500
          ? "Could not update availability — try again."
          : "Could not update availability. Please check the form and try again.";
      const message =
        err?.response?.data?.error || err?.response?.data?.message || fallback;
      setEditFormError(message);
    } finally {
      setEditSubmitting(false);
    }
  };

  // ---- Delete-confirm handlers -----------------------------------------
  const requestDelete = (record) => {
    setPendingDelete(record);
  };
  const cancelDelete = () => {
    setPendingDelete(null);
  };
  const confirmDelete = async () => {
    if (!pendingDelete || deleting || !userId) return;
    setDeleting(true);
    try {
      await callRemove(userId, pendingDelete._id);
      const removedId = pendingDelete._id;
      setListRecords((prev) => prev.filter((r) => r._id !== removedId));
      setPendingDelete(null);
      showToast("success", `Availability deleted for ${safeFullName}.`);
      if (typeof onAvailabilityChanged === "function") {
        try {
          onAvailabilityChanged();
        } catch (e) {
          /* parent's problem */
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      const fallback =
        status >= 500
          ? "Could not delete the availability window — try again."
          : "Could not delete the availability window. Please try again.";
      const message =
        err?.response?.data?.error || err?.response?.data?.message || fallback;
      setListError(message);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;
  // Defense-in-depth: if for any reason the caller opened without a userId,
  // refuse to render the form rather than firing a bad request. The
  // affordance in EditShiftModal hides itself when `userId` is missing, so
  // this is a belt-and-suspenders check.
  if (!userId) return null;

  // ---- Render helpers ---------------------------------------------------
  const createTimeRangeError = timeRangeErrorFor(createForm);
  const editTimeRangeError = timeRangeErrorFor(editForm);

  const renderCreateMode = () => (
    <form
      className="admin-set-avail-modal__create"
      onSubmit={handleCreateSubmit}
      noValidate
      data-testid="admin-set-avail-create-form"
    >
      {createFormError ? (
        <div className="admin-set-avail-modal__banner-error" role="alert">
          {createFormError}
        </div>
      ) : null}

      {/* SCHED-AVAIL-DASHBOARD-002-B — Window type segmented control.
            Default = Recurring (Slice 1 behavior); One-off swaps the Day
            dropdown for a single-date picker (Zuri spec §3.1 + §3.3). */}
      <fieldset
        className="admin-set-avail-modal__window-type"
        data-testid="admin-set-avail-window-type"
      >
        <legend className="admin-set-avail-modal__label">
          Window type
          <span
            className="admin-set-avail-modal__required"
            aria-hidden="true"
          >
            {" *"}
          </span>
        </legend>
        <div
          className="admin-set-avail-modal__segmented"
          role="radiogroup"
          aria-label="Window type"
        >
          <button
            type="button"
            role="radio"
            aria-checked={!!createForm.isRecurring}
            className={
              "admin-set-avail-modal__segmented-btn" +
              (createForm.isRecurring
                ? " admin-set-avail-modal__segmented-btn--active"
                : "")
            }
            onClick={() => switchWindowType(true)}
            disabled={createSubmitting}
            data-testid="admin-set-avail-window-type-recurring"
          >
            Recurring weekly
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={!createForm.isRecurring}
            className={
              "admin-set-avail-modal__segmented-btn" +
              (!createForm.isRecurring
                ? " admin-set-avail-modal__segmented-btn--active"
                : "")
            }
            onClick={() => switchWindowType(false)}
            disabled={createSubmitting}
            data-testid="admin-set-avail-window-type-oneoff"
          >
            Single occurrence
          </button>
        </div>
      </fieldset>

      {createForm.isRecurring ? (
        <>
          <label
            className="admin-set-avail-modal__label"
            htmlFor="admin-set-avail-day"
          >
            Day
            <span
              className="admin-set-avail-modal__required"
              aria-hidden="true"
            >
              {" *"}
            </span>
          </label>
          <select
            id="admin-set-avail-day"
            className="admin-set-avail-modal__select"
            value={createForm.dayOfWeek}
            onChange={(e) =>
              handleCreateField("dayOfWeek", parseInt(e.target.value, 10))
            }
            disabled={createSubmitting}
            data-testid="admin-set-avail-day"
          >
            {DAY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </>
      ) : (
        <>
          <label
            className="admin-set-avail-modal__label"
            htmlFor="admin-set-avail-effective-date"
          >
            Date
            <span
              className="admin-set-avail-modal__required"
              aria-hidden="true"
            >
              {" *"}
            </span>
          </label>
          <input
            id="admin-set-avail-effective-date"
            type="date"
            className="admin-set-avail-modal__input"
            value={createForm.effectiveDateYmd || ""}
            onChange={(e) => handleEffectiveDateChange(e.target.value)}
            disabled={createSubmitting}
            min={todayYmd()}
            required
            data-testid="admin-set-avail-effective-date"
          />
          <p
            className="admin-set-avail-modal__helper"
            id="admin-set-avail-effective-date-helper"
          >
            {createForm.effectiveDateYmd
              ? `${formatYmdHuman(createForm.effectiveDateYmd)} (MST)`
              : "Times shown in MST"}
          </p>
        </>
      )}

      <div className="admin-set-avail-modal__time-row">
        <div className="admin-set-avail-modal__time-field">
          <label
            className="admin-set-avail-modal__label"
            htmlFor="admin-set-avail-start"
          >
            Start time (MST)
            <span
              className="admin-set-avail-modal__required"
              aria-hidden="true"
            >
              {" *"}
            </span>
          </label>
          <input
            id="admin-set-avail-start"
            type="time"
            className="admin-set-avail-modal__input"
            value={createForm.startTime}
            onChange={(e) => handleCreateField("startTime", e.target.value)}
            disabled={createSubmitting}
            required
            data-testid="admin-set-avail-start"
          />
        </div>
        <div className="admin-set-avail-modal__time-field">
          <label
            className="admin-set-avail-modal__label"
            htmlFor="admin-set-avail-end"
          >
            End time (MST)
            <span
              className="admin-set-avail-modal__required"
              aria-hidden="true"
            >
              {" *"}
            </span>
          </label>
          <input
            id="admin-set-avail-end"
            type="time"
            className="admin-set-avail-modal__input"
            value={createForm.endTime}
            onChange={(e) => handleCreateField("endTime", e.target.value)}
            disabled={createSubmitting}
            required
            data-testid="admin-set-avail-end"
            aria-describedby={
              createTimeRangeError ? "admin-set-avail-end-error" : undefined
            }
          />
        </div>
      </div>
      {createTimeRangeError ? (
        <p
          id="admin-set-avail-end-error"
          className="admin-set-avail-modal__inline-error"
          role="alert"
        >
          {createTimeRangeError}
        </p>
      ) : null}

      <label
        className="admin-set-avail-modal__label"
        htmlFor="admin-set-avail-notes"
      >
        Notes (optional)
      </label>
      <textarea
        id="admin-set-avail-notes"
        className="admin-set-avail-modal__textarea"
        rows={2}
        maxLength={MAX_NOTES_LENGTH}
        value={createForm.notes || ""}
        onChange={(e) => handleCreateField("notes", e.target.value)}
        disabled={createSubmitting}
        placeholder="Capture why this is being set on behalf, if useful."
        data-testid="admin-set-avail-notes"
      />
      <p
        className="admin-set-avail-modal__notes-counter"
        aria-live="polite"
      >
        {(createForm.notes || "").length}/{MAX_NOTES_LENGTH}
      </p>

      <div
        className="admin-set-avail-modal__info-banner"
        role="note"
        data-testid="admin-set-avail-info-banner"
      >
        You are setting this availability on {safeFirstName}&apos;s behalf.{" "}
        {safeFirstName} will not receive a notification; the change is logged
        in the audit trail.
      </div>

      <div className="admin-set-avail-modal__footer">
        <button
          type="button"
          className="admin-set-avail-modal__expander"
          onClick={openListMode}
          disabled={createSubmitting}
          data-testid="admin-set-avail-expander"
        >
          Manage all availability for {safeFirstName} &rsaquo;
        </button>
        <div className="admin-set-avail-modal__footer-spacer" />
        <button
          type="button"
          className="admin-set-avail-modal__cancel"
          onClick={onClose}
          disabled={createSubmitting}
          data-testid="admin-set-avail-cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="admin-set-avail-modal__save"
          disabled={!createValid || createSubmitting}
          data-testid="admin-set-avail-save"
        >
          {createSubmitting ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );

  const renderListMode = () => {
    if (listLoading) {
      return (
        <p
          className="admin-set-avail-modal__list-loading"
          aria-live="polite"
          data-testid="admin-set-avail-list-loading"
        >
          Loading {safeFirstName}&apos;s availability...
        </p>
      );
    }
    if (listError) {
      return (
        <div className="admin-set-avail-modal__list-error" role="alert">
          {listError}
        </div>
      );
    }
    if (sortedRecords.length === 0) {
      // Empty state per §10K.6
      return (
        <div
          className="admin-set-avail-modal__empty"
          role="status"
          data-testid="admin-set-avail-empty"
        >
          <div
            className="admin-set-avail-modal__empty-icon"
            aria-hidden="true"
          >
            {/* Simple muted calendar glyph; matches StaffAvailabilityPage
                styling cue and avoids a new icon dependency. */}
            &#x1F4C5;
          </div>
          <h3 className="admin-set-avail-modal__empty-heading">
            No availability on file
          </h3>
          <p className="admin-set-avail-modal__empty-body">
            {safeFirstName} has not set their own availability yet. Use the
            form on the previous screen to add the first window on their
            behalf.
          </p>
          <button
            type="button"
            className="admin-set-avail-modal__back-link admin-set-avail-modal__empty-back"
            onClick={backToCreate}
            data-testid="admin-set-avail-empty-back"
          >
            &lsaquo; Back to set new
          </button>
        </div>
      );
    }
    return (
      <ul
        className="admin-set-avail-modal__list"
        aria-label={`Availability windows for ${safeFullName}`}
        data-testid="admin-set-avail-list"
      >
        {sortedRecords.map((record) => {
          const day = DAY_SHORT[record.dayOfWeek] || dayLabel(record.dayOfWeek);
          const isEditing = editingId === record._id;
          const isDimmed = editingId && editingId !== record._id;
          const isAnchored =
            !!anchorAvailabilityId && record._id === anchorAvailabilityId;
          const editLabel = `Edit ${dayLabel(record.dayOfWeek)} ${
            record.startTime
          }-${record.endTime}`;
          const deleteLabel = `Delete ${dayLabel(record.dayOfWeek)} ${
            record.startTime
          }-${record.endTime}`;
          return (
            <li
              key={record._id}
              ref={isAnchored ? anchorRowRef : undefined}
              className={
                "admin-set-avail-modal__row" +
                (isDimmed ? " admin-set-avail-modal__row--dimmed" : "") +
                (isAnchored ? " admin-set-avail-modal__row--anchored" : "")
              }
              data-testid={`admin-set-avail-row-${record._id}`}
              data-anchored={isAnchored ? "true" : undefined}
            >
              {isEditing ? (
                <form
                  className="admin-set-avail-modal__edit-form"
                  onSubmit={handleEditSubmit}
                  noValidate
                  data-testid={`admin-set-avail-edit-form-${record._id}`}
                >
                  <p className="admin-set-avail-modal__edit-context">
                    Editing: {day} {formatTime12h(record.startTime)} -{" "}
                    {formatTime12h(record.endTime)}
                  </p>

                  {editFormError ? (
                    <div
                      className="admin-set-avail-modal__banner-error"
                      role="alert"
                    >
                      {editFormError}
                    </div>
                  ) : null}

                  <label
                    className="admin-set-avail-modal__label"
                    htmlFor={`admin-set-avail-edit-day-${record._id}`}
                  >
                    Day
                  </label>
                  <select
                    id={`admin-set-avail-edit-day-${record._id}`}
                    className="admin-set-avail-modal__select"
                    value={editForm.dayOfWeek}
                    onChange={(e) =>
                      handleEditField(
                        "dayOfWeek",
                        parseInt(e.target.value, 10)
                      )
                    }
                    disabled={editSubmitting}
                  >
                    {DAY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>

                  <div className="admin-set-avail-modal__time-row">
                    <div className="admin-set-avail-modal__time-field">
                      <label
                        className="admin-set-avail-modal__label"
                        htmlFor={`admin-set-avail-edit-start-${record._id}`}
                      >
                        Start time (MST)
                      </label>
                      <input
                        id={`admin-set-avail-edit-start-${record._id}`}
                        type="time"
                        className="admin-set-avail-modal__input"
                        value={editForm.startTime}
                        onChange={(e) =>
                          handleEditField("startTime", e.target.value)
                        }
                        disabled={editSubmitting}
                      />
                    </div>
                    <div className="admin-set-avail-modal__time-field">
                      <label
                        className="admin-set-avail-modal__label"
                        htmlFor={`admin-set-avail-edit-end-${record._id}`}
                      >
                        End time (MST)
                      </label>
                      <input
                        id={`admin-set-avail-edit-end-${record._id}`}
                        type="time"
                        className="admin-set-avail-modal__input"
                        value={editForm.endTime}
                        onChange={(e) =>
                          handleEditField("endTime", e.target.value)
                        }
                        disabled={editSubmitting}
                      />
                    </div>
                  </div>
                  {editTimeRangeError ? (
                    <p
                      className="admin-set-avail-modal__inline-error"
                      role="alert"
                    >
                      {editTimeRangeError}
                    </p>
                  ) : null}

                  <label className="admin-set-avail-modal__checkbox-row">
                    <input
                      type="checkbox"
                      checked={!!editForm.isRecurring}
                      onChange={(e) =>
                        handleEditField("isRecurring", e.target.checked)
                      }
                      disabled={editSubmitting}
                    />
                    <span>Recurring weekly</span>
                  </label>

                  <label
                    className="admin-set-avail-modal__label"
                    htmlFor={`admin-set-avail-edit-notes-${record._id}`}
                  >
                    Notes (optional)
                  </label>
                  <textarea
                    id={`admin-set-avail-edit-notes-${record._id}`}
                    className="admin-set-avail-modal__textarea"
                    rows={2}
                    maxLength={MAX_NOTES_LENGTH}
                    value={editForm.notes || ""}
                    onChange={(e) => handleEditField("notes", e.target.value)}
                    disabled={editSubmitting}
                  />

                  <div className="admin-set-avail-modal__edit-actions">
                    <button
                      type="button"
                      className="admin-set-avail-modal__cancel"
                      onClick={cancelEdit}
                      disabled={editSubmitting}
                      data-testid={`admin-set-avail-edit-cancel-${record._id}`}
                    >
                      Cancel edit
                    </button>
                    <button
                      type="submit"
                      className="admin-set-avail-modal__save"
                      disabled={!editValid || editSubmitting}
                      data-testid={`admin-set-avail-edit-save-${record._id}`}
                    >
                      {editSubmitting ? "Saving..." : "Save changes"}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="admin-set-avail-modal__row-body">
                    <div className="admin-set-avail-modal__row-line1">
                      {day} {formatTime12h(record.startTime)} -{" "}
                      {formatTime12h(record.endTime)}
                    </div>
                    <div className="admin-set-avail-modal__row-line2">
                      {record.isRecurring ? "Recurring" : "Single occurrence"}
                      {record.setByAdmin ? (
                        <>
                          {" "}
                          &middot;{" "}
                          <span
                            className="admin-set-avail-modal__badge-admin"
                            data-testid={`admin-set-avail-badge-admin-${record._id}`}
                          >
                            Set by admin
                          </span>
                        </>
                      ) : (
                        <>
                          {" "}
                          &middot;{" "}
                          <span className="admin-set-avail-modal__attribution-staff">
                            Set by {safeFirstName}
                          </span>
                        </>
                      )}
                      {record.notes ? (
                        <span
                          className="admin-set-avail-modal__row-notes"
                          title={record.notes}
                        >
                          {" "}
                          &middot; {record.notes}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="admin-set-avail-modal__row-actions">
                    <button
                      type="button"
                      className={
                        "admin-set-avail-modal__row-edit" +
                        (isAnchored
                          ? " admin-set-avail-modal__row-edit--anchored"
                          : "")
                      }
                      onClick={() => openEdit(record)}
                      disabled={!!editingId || deleting}
                      aria-label={editLabel}
                      data-testid={`admin-set-avail-edit-${record._id}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="admin-set-avail-modal__row-delete"
                      onClick={() => requestDelete(record)}
                      disabled={!!editingId || deleting}
                      aria-label={deleteLabel}
                      data-testid={`admin-set-avail-delete-${record._id}`}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </li>
          );
        })}
        <li className="admin-set-avail-modal__list-cta-row">
          <button
            type="button"
            className="admin-set-avail-modal__add-another"
            onClick={setAnotherWindow}
            disabled={!!editingId || deleting}
            data-testid="admin-set-avail-add-another"
          >
            + Set another availability window
          </button>
        </li>
      </ul>
    );
  };

  return (
    <>
      <div
        className="admin-set-avail-modal__backdrop"
        aria-hidden="true"
        data-testid="admin-set-avail-modal-backdrop"
      />
      <div
        className="admin-set-avail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-set-avail-modal-heading"
        data-testid="admin-set-avail-modal"
      >
        <header className="admin-set-avail-modal__header">
          {view === "list" ? (
            <button
              type="button"
              className="admin-set-avail-modal__back-link"
              onClick={backToCreate}
              disabled={!!editingId || deleting}
              data-testid="admin-set-avail-back"
            >
              &lsaquo; Back to set new
            </button>
          ) : null}
          <div className="admin-set-avail-modal__titles">
            <h2
              id="admin-set-avail-modal-heading"
              className="admin-set-avail-modal__heading"
            >
              {view === "list"
                ? `Manage availability for ${safeFullName}`
                : `Set availability for ${safeFullName}`}
            </h2>
            <p className="admin-set-avail-modal__subtitle">
              {view === "list"
                ? `${sortedRecords.length} window${
                    sortedRecords.length === 1 ? "" : "s"
                  } on file`
                : "Admin-on-behalf entry"}
            </p>
          </div>
          <button
            type="button"
            className="admin-set-avail-modal__close"
            onClick={onClose}
            disabled={createSubmitting || editSubmitting || deleting}
            aria-label="Close availability modal"
            data-testid="admin-set-avail-close"
          >
            &times;
          </button>
        </header>

        <div className="admin-set-avail-modal__body">
          {view === "create" ? renderCreateMode() : renderListMode()}
        </div>

        {view === "list" ? (
          <div className="admin-set-avail-modal__footer admin-set-avail-modal__footer-list">
            <button
              type="button"
              className="admin-set-avail-modal__save"
              onClick={onClose}
              disabled={!!editingId || deleting}
              data-testid="admin-set-avail-list-close"
            >
              Close
            </button>
          </div>
        ) : null}
      </div>

      {pendingDelete ? (
        <>
          <div
            className="admin-set-avail-modal__backdrop admin-set-avail-modal__backdrop--sub"
            aria-hidden="true"
          />
          <div
            className="admin-set-avail-modal admin-set-avail-modal--sub"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="admin-set-avail-delete-heading"
            aria-describedby="admin-set-avail-delete-body"
            data-testid="admin-set-avail-delete-modal"
          >
            <header className="admin-set-avail-modal__header">
              <div className="admin-set-avail-modal__titles">
                <h2
                  id="admin-set-avail-delete-heading"
                  className="admin-set-avail-modal__heading"
                >
                  Delete availability window?
                </h2>
              </div>
              <button
                type="button"
                className="admin-set-avail-modal__close"
                onClick={cancelDelete}
                disabled={deleting}
                aria-label="Close delete confirmation"
              >
                &times;
              </button>
            </header>
            <div className="admin-set-avail-modal__body">
              <p id="admin-set-avail-delete-body">
                You are deleting:
                <br />
                {DAY_SHORT[pendingDelete.dayOfWeek] ||
                  dayLabel(pendingDelete.dayOfWeek)}{" "}
                {formatTime12h(pendingDelete.startTime)} -{" "}
                {formatTime12h(pendingDelete.endTime)}
                {pendingDelete.isRecurring ? " · Recurring" : ""}
              </p>
              <p className="admin-set-avail-modal__delete-note">
                This is admin-on-behalf data entry. {safeFirstName} will not
                receive a notification. The deletion is logged in the audit
                trail.
              </p>
            </div>
            <div className="admin-set-avail-modal__footer">
              <div className="admin-set-avail-modal__footer-spacer" />
              <button
                type="button"
                className="admin-set-avail-modal__cancel"
                onClick={cancelDelete}
                disabled={deleting}
                data-testid="admin-set-avail-delete-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-set-avail-modal__delete-confirm"
                onClick={confirmDelete}
                disabled={deleting}
                data-testid="admin-set-avail-delete-confirm"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </>
      ) : null}

      {toast ? (
        <div
          className={`admin-set-avail-modal__toast admin-set-avail-modal__toast--${toast.variant}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      ) : null}
    </>
  );
};

export default AdminSetAvailabilityModal;
